import { isPromise } from '../type-check';
/**
 * Vue 自己的 `Vue.extend()` 不支持传入异步组件，因此这里提供了一个很好用的方法
 *
 * @example
 * ```ts
 * // 基类
 * const EditorBase = Vue.extend({
 *   inject: { ... },
 *   methods: { ... },
 * });
 *
 * const extendEditor = vueMakeExtendFactory(EditorBase);
 *
 * const poweredInput = extendEditor(myInput);
 * const poweredCheckbox = extendEditor(() => import('./myCheckbox')); // 异步组件！
 * ```
 */
export function vueMakeExtendFactory(base, options) {
  const patchedLUT = new WeakMap();
  const excludeOptions = options === null || options === void 0 ? void 0 : options.excludeOptions;
  const actualMakePatchedVueComponent = comp => {
    if (typeof comp === 'function') {
      if (!('options' in comp)) {
        return (actualResolve, reject) => {
          const resolve = ans => actualResolve(getPatchedVueComponent(ans));
          const res = comp(resolve, reject);
          if (isPromise(res)) {
            return res.then(resolve, reject);
          }
          if (isPromise(res === null || res === void 0 ? void 0 : res.component)) {
            // 如果工厂函数的返回值不是一个promise，那么工厂函数返回的应该是一个配置对象
            // 见https://cn.vuejs.org/v2/guide/components-dynamic-async.html#%E5%A4%84%E7%90%86%E5%8A%A0%E8%BD%BD%E7%8A%B6%E6%80%81
            return {
              ...res,
              component: res.component.then(comp => getPatchedVueComponent(comp))
            };
          }
        };
      }
      // 普通的构造函数组件
      // 已经改造过，不需要改造了
      if (excludeOptions && excludeOptions(comp.options)) return comp;
      return base.extend(comp);
    }
    // 这是一个普通的 object
    if (excludeOptions && excludeOptions(comp)) return comp;
    return base.extend(comp);
  };
  const getPatchedVueComponent = comp => {
    if (!comp) return comp;
    if (typeof comp !== 'object' && typeof comp !== 'function') return comp;
    let cached = patchedLUT.get(comp);
    if (!cached) patchedLUT.set(comp, cached = actualMakePatchedVueComponent(comp));
    return cached;
  };
  return getPatchedVueComponent;
}
//# sourceMappingURL=vueMakeExtendFactory.js.map
